import { computed } from 'vue';
import { TranslateResult } from 'vue-i18n';

import { Icon } from '../models/utilities/icon.enum';
import { Variant } from '../models/utilities/variant.enum';
import {
  BottomRowButton,
  BottomRowButtonAction,
  BottomRowButtonTarget,
} from '../models/entities/information-card.model';
import {
  Maybe,
  BookingAppointmentConfirmation,
  BookingStatus,
  SchedulePreference,
  TestDriveVehicle,
} from '../graphql/schema';
import { BookingDropdownOption } from '../models/entities/booking.model';
import { VehicleInformation } from '../models/entities/vehicle-information.model';
import { VehicleTrackingInfo } from '../models/entities/tracking.model';
import { DatesAvailabilityType } from '../models/entities/calendar.model';

import { useStore } from './store.composable';

import BookingManagementService from '../services/booking-management.service';
import { i18n } from '../services/translation.service';
import VehicleInformationService from '../services/components/vehicle-information.service';
import DateService from '../services/date.service';
import TrackingService from '../services/tracking.service';

import {
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_TYPE,
  MONTHS_TO_CALL,
} from '../models/constants';

const useBooking = () => {
  // ----------------------------------------
  // variables
  const { state, getter, dispatch } = useStore();

  // ----------------------------------------
  // computed
  const bookingVehicles = computed(
    (): BookingAppointmentConfirmation[] =>
      state.scheduledTestDrives.userTestDriveBookings
  );

  // ----------------------------------------
  // methods
  const formatTagText = (status: string): TranslateResult | string =>
    status ? i18n.t(`otbs.informationCard.tag.${status.toLowerCase()}`) : '';

  const getBookingActions = (
    status: BookingStatus,
    bookingId: string,
    onlineStoreDeeplink: string
  ): BottomRowButton[] => {
    const buttons: BottomRowButton[] = [];
    if (status == BookingStatus.BOOKED && getter.bookingCancellationLink) {
      buttons.push({
        label: i18n.t('otbs.informationCard.secondaryButton.booking'),
        icon: Icon.TRASH,
        variant: Variant.SECONDARY,
        action: BottomRowButtonAction.MODAL,
        link: BookingManagementService.getBookingManagementUrl(
          getter.bookingCancellationLink,
          bookingId
        ),
        target: BottomRowButtonTarget.CANCELLATION,
      });
      if (getter.isRescheduleActive && getter.bookingRescheduleLink) {
        buttons.push({
          label: i18n.t('otbs.informationCard.primaryButton.booking'),
          icon: Icon.CALENDAR,
          variant: Variant.PRIMARY,
          action: BottomRowButtonAction.MODAL,
          link: BookingManagementService.getBookingManagementUrl(
            getter.bookingRescheduleLink,
            bookingId
          ),
          target: BottomRowButtonTarget.RESCHEDULE,
        });
      }
    }
    if (status == BookingStatus.DONE && onlineStoreDeeplink) {
      buttons.push({
        label: i18n.t('otbs.informationCard.primaryButton.purchase'),
        icon: Icon.SHOP,
        variant: Variant.PRIMARY,
        action: BottomRowButtonAction.LINK,
        link: BookingManagementService.getBookingManagementUrl(
          onlineStoreDeeplink,
          bookingId
        ),
        trackingLabel: TRACKING_EVENT_LABEL.BUY_VEHICLE,
      });
    }
    return buttons;
  };

  const getInstructions = (status: BookingStatus, bookingId: string) => {
    return status === BookingStatus.DONE &&
      state.configuration.links.myDocumentsArea
      ? {
          title: i18n.t('otbs.informationCard.documents.title'),
          label: i18n.t('otbs.informationCard.documents.label'),
          link: BookingManagementService.getBookingManagementUrl(
            state.configuration.links.myDocumentsArea,
            bookingId
          ),
          icon: 'brochures',
          trackingLabel: TRACKING_EVENT_LABEL.SEE_DOCUMENTS,
        }
      : {};
  };

  const getTestDriveTime = (schedule: SchedulePreference): string =>
    DateService.getCompleteTime(schedule, getter.localeCode);

  const getVehicleInformation = (
    vehicle: TestDriveVehicle
  ): VehicleInformation =>
    VehicleInformationService.getVehicleInformation(vehicle);

  const getVansInformation = (van: TestDriveVehicle): VehicleInformation =>
    VehicleInformationService.getVehicleInformation(van);

  const trackBookingClickEvent = (
    label: TRACKING_EVENT_LABEL,
    trackingVehicle?: VehicleTrackingInfo,
    trackingDealerId?: string
  ): void => {
    const event = TrackingService.buildEvent(TRACKING_EVENT_TYPE.CLICK, {
      action: TRACKING_EVENT_ACTION.GENERAL,
      category: TRACKING_EVENT_CATEGORY.LINK,
      label,
      vehicle: trackingVehicle,
      retailerId: trackingDealerId,
    });

    TrackingService.track(event);
  };

  const getTestDriveBookingsCalendar = async (
    vehicleId: string,
    dealerId: string,
    date?: Date
  ): Promise<void> => {
    const firstDayOfCurrentMonth = DateService.firstDayOfMonth();
    const startDate = DateService.formatDateForVBS(
      date ?? firstDayOfCurrentMonth
    );
    const endDate = DateService.formatDateForVBS(
      DateService.addMonths(firstDayOfCurrentMonth, MONTHS_TO_CALL)
    );

    await dispatch('getTestDriveBookingsCalendar', {
      dealerId,
      startDate,
      endDate,
      vehicleId,
      language: state.configuration.locale.language,
      countryCode: state.configuration.locale.countryCode,
    });
  };

  const getTestDriveBookingsTimeSlots = async (
    vehicleId: string,
    dealerId: string,
    selectedDay: Date
  ): Promise<boolean> => {
    return await dispatch('getTestDriveBookingsTimeSlots', {
      dealerId,
      vehicleId,
      selectedDay,
      language: state.configuration.locale.language,
      countryCode: state.configuration.locale.countryCode,
    }).then((response) => response);
  };

  const getFirstAvailableBookingDay = computed(() =>
    state.scheduledTestDrives.testDriveBookingsCalendar.find(
      (date) => date.available
    )
  );

  const bookingCalendar = computed(
    (): Maybe<DatesAvailabilityType[]> => getter.testDriveBookingsCalendar
  );

  const bookingTimeslots = computed(
    (): Maybe<BookingDropdownOption[]> => getter.testDriveBookingsTimeSlots
  );

  const getBookingDatetimeLabels = computed(() => {
    return {
      day: new Intl.DateTimeFormat(getter.localeCode, {
        dateStyle: 'full',
      }).format(new Date(state.otbcFlow.selectedDay)),
      time: state.otbcFlow.selectedPeriod.label,
    };
  });

  return {
    bookingCalendar,
    bookingTimeslots,
    bookingVehicles,
    formatTagText,
    getBookingActions,
    getBookingDatetimeLabels,
    getFirstAvailableBookingDay,
    getInstructions,
    getTestDriveTime,
    getTestDriveBookingsCalendar,
    getTestDriveBookingsTimeSlots,
    getVehicleInformation,
    getVansInformation,
    trackBookingClickEvent,
  };
};

export default useBooking;
